import config from '../../config';
import { getAuthHeaders, getUserIdFromToken } from '../api/common';

// URL base64 to Uint8Array conversion for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

class PushNotificationManager {
  constructor() {
    this.vapidPublicKey = null;
    this.subscription = null;
    this.detachPromise = Promise.resolve();
    this.isSupported = this.checkSupport();
  }

  isOptedOut() {
    const userId = getUserIdFromToken();
    return Boolean(userId && localStorage.getItem(`pushNotificationsDisabled:${userId}`) === 'true');
  }

  setOptedOut(disabled) {
    const userId = getUserIdFromToken();
    if (userId) localStorage.setItem(`pushNotificationsDisabled:${userId}`, String(disabled));
  }

  canAutoSubscribe() {
    return Boolean(getUserIdFromToken()) && !this.isOptedOut() && this.getPermissionStatus() === 'granted';
  }

  // Check if push notifications are supported
  checkSupport() {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  // Get current notification permission status
  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  // Request notification permission from user
  async requestNotificationPermission() {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported in this browser');
      return 'unsupported';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      
      if (permission === 'granted') {
        // Store permission status
        localStorage.setItem('notificationPermission', 'granted');
      }
      
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Fetch VAPID public key from backend
  async fetchVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await fetch(`${config.api.url}/push/public-key`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch VAPID public key: ${response.status}`);
      }

      const data = await response.json();
      this.vapidPublicKey = data.publicKey;
      return this.vapidPublicKey;
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      throw error;
    }
  }

  // Subscribe to push notifications
  async subscribeToPushNotifications() {
    const userId = getUserIdFromToken();
    if (!userId || this.isOptedOut()) return null;
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    const permission = this.getPermissionStatus();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    try {
      await this.detachPromise;
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Check if already subscribed
      let existingSubscription = await registration.pushManager.getSubscription();
      const previousOwner = localStorage.getItem('pushSubscriptionOwner');
      if (existingSubscription && previousOwner && previousOwner !== String(userId)) {
        await existingSubscription.unsubscribe();
        existingSubscription = null;
      }
      if (getUserIdFromToken() !== userId || this.isOptedOut()) return null;
      if (existingSubscription) {
        console.log('Already subscribed to push notifications, re-syncing with backend');
        this.subscription = existingSubscription;
        // Always re-send to backend — the backend may have lost the record
        // (e.g. DB reset, migration, redeployment). This is idempotent if the
        // backend upserts on endpoint.
        await this.sendSubscriptionToBackend(existingSubscription);
        if (getUserIdFromToken() !== userId || this.isOptedOut()) return null;
        localStorage.setItem('pushSubscriptionOwner', String(userId));
        localStorage.setItem('pushSubscriptionEndpoint', existingSubscription.endpoint);
        localStorage.setItem('pushSubscriptionActive', 'true');
        return existingSubscription;
      }

      // Fetch VAPID public key
      const vapidPublicKey = await this.fetchVapidPublicKey();
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      if (getUserIdFromToken() !== userId || this.isOptedOut()) {
        await subscription.unsubscribe();
        return null;
      }

      this.subscription = subscription;

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);
      if (getUserIdFromToken() !== userId || this.isOptedOut()) return null;

      // Store subscription status
      localStorage.setItem('pushSubscriptionActive', 'true');
      localStorage.setItem('pushSubscriptionOwner', String(userId));
      localStorage.setItem('pushSubscriptionEndpoint', subscription.endpoint);

      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  // Send subscription to backend for storage
  async sendSubscriptionToBackend(subscription) {
    try {
      const response = await fetch(`${config.api.url}/push/subscribe`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(subscription.toJSON())
      });

      if (!response.ok) {
        throw new Error(`Failed to send subscription to backend: ${response.status}`);
      }

      console.log('Subscription sent to backend successfully');
      return true;
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribeFromPushNotifications({ preservePreference = false } = {}) {
    if (!preservePreference) this.setOptedOut(true);
    try {
      localStorage.removeItem('pushSubscriptionActive');
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        console.log('No active subscription to unsubscribe from');
        return true;
      }

      // Unsubscribe from push manager
      const successful = await subscription.unsubscribe();

      if (successful) {
        console.log('Unsubscribed from push notifications');
        
        // Notify backend to remove subscription
        await this.removeSubscriptionFromBackend(subscription);

        this.subscription = null;
        localStorage.removeItem('pushSubscriptionEndpoint');
        localStorage.removeItem('pushSubscriptionOwner');
      }

      return successful;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  }

  // Capture authentication before sign-out clears it. keepalive lets the removal
  // finish during navigation; local unsubscribe also invalidates the endpoint.
  detachOnSignOut(token) {
    const endpoint = localStorage.getItem('pushSubscriptionEndpoint');
    const headers = token ? { ...getAuthHeaders(), Authorization: `Bearer ${token}` } : getAuthHeaders();
    localStorage.removeItem('pushSubscriptionActive');
    localStorage.removeItem('pushSubscriptionEndpoint');
    localStorage.removeItem('pushSubscriptionOwner');
    this.subscription = null;
    if (endpoint && headers.Authorization) {
      fetch(`${config.api.url}/push/unsubscribe`, {
        method: 'DELETE', headers, body: JSON.stringify({ endpoint }), keepalive: true,
      }).catch(() => {});
    }
    if (this.checkSupport()) {
      this.detachPromise = navigator.serviceWorker.ready.then((registration) => registration.pushManager.getSubscription())
        .then((subscription) => {
          if (subscription && (!endpoint || subscription.endpoint === endpoint)) return subscription.unsubscribe();
          return undefined;
        }).catch(() => {});
      return this.detachPromise;
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(subscription) {
    try {
      const response = await fetch(`${config.api.url}/push/unsubscribe`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });

      if (!response.ok) {
        console.warn(`Failed to remove subscription from backend: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
      // Don't throw - local unsubscribe was successful
      return false;
    }
  }

  // Get current subscription
  async getCurrentSubscription() {
    if (!this.isSupported) return null;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription;
    } catch (error) {
      console.error('Error getting current subscription:', error);
      return null;
    }
  }

  // Check if currently subscribed
  async isSubscribed() {
    const subscription = await this.getCurrentSubscription();
    return subscription !== null;
  }

  // A browser subscription can outlive its active registration on the server.
  // Check both sides, and reuse the endpoint when registration needs repair.
  async ensureRegistration() {
    const userId = getUserIdFromToken();
    if (!this.canAutoSubscribe()) return false;
    const subscription = await this.getCurrentSubscription();
    if (getUserIdFromToken() !== userId || !this.canAutoSubscribe()) return false;
    if (subscription) {
      const response = await fetch(`${config.api.url}/push/subscription`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(`Failed to check device registration: ${response.status}`);
      const data = await response.json();
      if (getUserIdFromToken() !== userId || !this.canAutoSubscribe()) return false;
      if (data.subscriptions?.some((device) => device.endpoint === subscription.endpoint)) return true;
    }
    return Boolean(await this.subscribeToPushNotifications());
  }

  // Update subscription (re-subscribe)
  async updateSubscription() {
    try {
      if (!this.canAutoSubscribe()) return false;
      await this.unsubscribeFromPushNotifications({ preservePreference: true });
      await this.subscribeToPushNotifications();
      return true;
    } catch (error) {
      console.error('Error updating subscription:', error);
      return false;
    }
  }

  // Request permission and subscribe in one step
  async requestAndSubscribe() {
    const permission = await this.requestNotificationPermission();
    
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    this.setOptedOut(false);
    const subscription = await this.subscribeToPushNotifications();
    if (!subscription) throw new Error('Sign in before enabling notifications.');
    return subscription;
  }
}

// Export singleton instance
export const pushNotificationManager = new PushNotificationManager();
export default pushNotificationManager;
